'use no memo'

import { HStack, Spread, Text, VStack } from '@/components/Atoms'
import { Spinner } from '@/components/Spinner'
import { resetFiltersPanel } from '@/lib/utils/filters'
import { columnVisibilityFamily, DataTableName } from '@/stores/datatable'
import {
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components'
import { snappyEaseInOut } from '@repo/ui/lib/animations'
import { cn } from '@repo/ui/lib/utils'
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  ColumnSort,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowData,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { useAtom } from 'jotai'
import { OctagonXIcon, SearchXIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppliedFilter, AppliedFiltersBar } from './AppliedFiltersBar'
import { ColumnControls } from './ColumnControls'
import { Filter, FilterControls } from './FilterControls'
import SearchField from './FilterControls/Filters/SearchField'
import { LoadingState } from './LoadingState'
import { PageControls } from './PageControls'
import { PageSize, PageSizeSelector } from './PageSizeSelector'
import { TableMessage } from './TableMessage'
import { DataTableTotals, TotalsRow } from './TotalsRow'

export type RowStatus = 'default' | 'warning' | 'critical' | 'success' | 'info'

type DataTableProps<TData, TValue> = {
  name: DataTableName
  columns: ColumnDef<TData, TValue>[]
  filters?: Filter<TValue>[]
  appliedFilters?: AppliedFilter[]
  /**
   * Badges for what the page's configuration applies (e.g. selected assumptions), shown in the same bar as
   * the applied filters. Clicking one calls `onConfigBadgeClick` rather than opening the filters panel.
   */
  configBadges?: AppliedFilter[]
  onConfigBadgeClick?: () => void
  isPending: boolean
  error: Error | null
  data: TData[]
  rowCount: number
  rowSelection?: RowSelectionState
  page?: number
  pageSize: PageSize
  sort?: ColumnSort
  search?: {
    value: string
    setValue: React.Dispatch<React.SetStateAction<string>>
    className?: string
    placeholder?: string
    onSearch?: (value: string) => void
  }
  actions?: React.ReactNode
  toolbarActions?: React.ReactNode
  /**
   * Opens the row's own record. Opt-in: a table that does not pass it behaves exactly as before,
   * with no pointer affordance and no keyboard handler.
   *
   * Clicks that land on a link, a button or any other interactive descendant are ignored, so a cell
   * that already navigates somewhere else — an ERP linking to its customer — keeps winning over the
   * row. Text selection is ignored too: dragging across a cell to copy a value should not navigate.
   */
  onRowClick?: (row: TData) => void
  /**
   * Which rows `onRowClick` actually applies to. Defaults to all of them.
   *
   * For a table where only some rows have a record to open — a list that also carries rows nothing
   * has been raised for yet, or rows already closed — a row that is not clickable gets no pointer,
   * no `role="link"` and no keyboard handler, so the affordance never promises a destination that
   * is not there.
   */
  isRowClickable?: (row: TData) => boolean
  queryParams?: Record<string, unknown>
  initialColumnVisibility?: VisibilityState
  /**
   * Pins columns to the viewport edges while the rest scroll horizontally (TanStack pinning,
   * offsets from `getStart`/`getAfter`). Static — users don't toggle it. Every pinned column
   * MUST declare an explicit `size` in its ColumnDef: offsets are computed from column sizing,
   * and an auto-width pinned column would drift from its computed offset.
   */
  columnPinning?: ColumnPinningState
  contentSlot?: React.ReactNode
  /** Totals across every page for the current filters, keyed by column id; pinned as the first body row. */
  totals?: DataTableTotals
  isSortedClientSide?: boolean
  rowId?: string
  pendingMessage?: string
  getSubRows?: (row: TData) => TData[] | undefined
  expanded?: ExpandedState
  onExpandedChange?: (next: ExpandedState) => void
  onShowResults?: () => void
  onResetAllFilters?: () => void
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: PageSize) => void
  onSortChange?: (sort: ColumnSort) => void
  onRowSelectionChange?: (selection: RowSelectionState) => void
}

declare module '@tanstack/react-table' {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  /* TData and TValue are expected by Tanstack even though we don't use them */
  interface ColumnMeta<TData extends RowData, TValue> {
    displayTitle?: string
    hideInColumnsMenu?: boolean
    hideOnFirstLoad?: boolean
    cellClassName?: string
    headerClassName?: string
    rowSelected?: boolean
  }
  /* eslint-enable @typescript-eslint/no-unused-vars */
}

export type DataTableRow = { rowStatus?: RowStatus; localId?: string }

/**
 * Sticky positioning for a pinned column, applied on both the header and body cells. The cell
 * needs an OPAQUE background (scrolled columns slide underneath), and body cells must replicate
 * the row-level semantic backgrounds — hence the `group/row` variants mirroring TableRow's
 * `data-status`/`data-state` styling; a plain background would mask the row tint in the pinned
 * region. The boundary column on each side carries a soft shadow so the seam reads as an edge.
 */
function getPinningAttributes<TData, TValue>(
  column: Column<TData, TValue>,
  context: 'header' | 'cell',
): { className?: string; style?: React.CSSProperties } {
  const pinned = column.getIsPinned()
  if (!pinned) return {}

  const isBoundary =
    pinned === 'left' ? column.getIsLastColumn('left') : column.getIsFirstColumn('right')

  return {
    className: cn(
      'sticky z-10',
      context === 'header'
        ? 'bg-background-level-2'
        : cn(
            'bg-background-level-1',
            'group-data-[state=selected]/row:bg-muted',
            'group-data-[status=critical]/row:bg-critical-bg',
            'group-data-[status=success]/row:bg-success-bg',
            'group-data-[status=warning]/row:bg-warning-bg',
            'group-data-[status=info]/row:bg-info-bg',
          ),
      isBoundary &&
        (pinned === 'left'
          ? 'shadow-[4px_0_8px_-4px_rgba(0,0,0,0.12)]'
          : 'shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.12)]'),
    ),
    style: {
      left: pinned === 'left' ? column.getStart('left') : undefined,
      right: pinned === 'right' ? column.getAfter('right') : undefined,
      width: column.getSize(),
      minWidth: column.getSize(),
      maxWidth: column.getSize(),
    },
  }
}

/**
 * Whether the event came from content a cell renders through a portal — a dialog, a dropdown menu,
 * a popover, a tooltip. React events bubble along the React tree rather than the DOM, so a click on
 * a dialog's overlay reaches the row that opened it even though it sits in `document.body`. Without
 * this the reader dismisses a dialog and the row navigates out from under them.
 */
function isOutsideRow(row: EventTarget & Element, target: EventTarget | null): boolean {
  return target instanceof Node && !row.contains(target)
}

/**
 * Whether a click landed on something that handles its own activation — a link, a button, an input,
 * or anything inside one. Rows that navigate must not swallow the controls they contain.
 */
function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      'a, button, input, select, textarea, label, [role="button"], [role="menuitem"]',
    ) !== null
  )
}

export function DataTable<TData extends DataTableRow, TValue>(
  props: DataTableProps<TData, TValue>,
) {
  const { t } = useTranslation('common')
  const { pendingMessage = t('datatable.pendingMessage') } = props

  const [sorting, setSorting] = useState<SortingState>(props.sort ? [props.sort] : [])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useAtom(
    columnVisibilityFamily({ name: props.name, initial: props.initialColumnVisibility }),
  )
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({})
  const expanded = props.expanded ?? internalExpanded
  const handleExpandedChange = (
    updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState),
  ) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(expanded) : updaterOrValue
    if (props.onExpandedChange) {
      props.onExpandedChange(next)
    } else {
      setInternalExpanded(next)
    }
  }

  const pagination = {
    pageIndex: props.page ? props.page - 1 : 0,
    pageSize: props.pageSize,
  }

  const handlePaginationChange = (
    updaterOrValue: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue

    props.onPageChange(newPagination.pageIndex)
  }

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    setSorting((old) => {
      const newSort = typeof updaterOrValue === 'function' ? updaterOrValue(old) : updaterOrValue

      if (props.onSortChange) props.onSortChange(newSort[0])

      return newSort
    })
  }

  const handleFiltersPanelOpenChange = () => {
    setIsFiltersPanelOpen(!isFiltersPanelOpen)

    if (!isFiltersPanelOpen) {
      resetFiltersPanel<TValue>(props.filters ?? [], props.queryParams ?? {})
    }
  }

  const getTableOptions = <TData extends Record<string, unknown>>() => {
    const options: Record<string, unknown> = {}

    if (props.rowId) options.getRowId = (row: TData) => row[props.rowId as keyof TData]
    if (props.onRowSelectionChange) options.onRowSelectionChange = props.onRowSelectionChange

    return options
  }

  const table = useReactTable({
    data: props.data,
    columns: props.columns,
    rowCount: props.rowCount,
    manualSorting: !props.isSortedClientSide,
    manualPagination: true,
    autoResetPageIndex: false,
    enableMultiSort: false,
    initialState: {
      columnVisibility: props.initialColumnVisibility ?? {},
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      pagination,
      expanded,
      rowSelection: props.rowSelection ?? {},
      columnPinning: props.columnPinning ?? {},
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getSubRows: props.getSubRows,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: handleExpandedChange,
    ...getTableOptions(),
  })

  return (
    <VStack gap="md">
      {props.actions && (
        <HStack gap="sm" className="w-full">
          {props.actions}
        </HStack>
      )}

      <VStack gap="sm" className="w-full">
        {/* Data Table Header */}
        <Spread align="center">
          <Text tone="muted" as="span" className="flex items-center gap-2">
            {props.isPending && <Spinner size="xs" speed="medium" />}
            {props.isPending
              ? pendingMessage
              : t('datatable.returnedResults', { count: props.rowCount })}
          </Text>

          <HStack gap="sm">
            <HStack gap="sm">
              {props.search && (
                <HStack gap="sm">
                  <SearchField
                    className={props.search.className}
                    placeholder={props.search.placeholder}
                    value={props.search.value}
                    setValue={props.search.setValue}
                    onSearch={props.search.onSearch}
                    onShowResults={props.onShowResults}
                  />
                  <Separator orientation="vertical" className="h-6" />
                </HStack>
              )}

              {props.filters && (
                <FilterControls
                  table={table}
                  filters={props.filters}
                  activeFilterCount={props.appliedFilters?.length}
                  onShowResults={props.onShowResults}
                  onResetAllFilters={props.onResetAllFilters}
                  isOpen={isFiltersPanelOpen}
                  onOpenChange={handleFiltersPanelOpenChange}
                />
              )}
            </HStack>
            {props.toolbarActions && <HStack gap="sm">{props.toolbarActions}</HStack>}
            <ColumnControls table={table} />
          </HStack>
        </Spread>

        {/* Applied Filters */}
        <AppliedFiltersBar
          appliedFilters={[...(props.appliedFilters ?? []), ...(props.configBadges ?? [])]}
          label={props.configBadges?.length ? t('datatable.applied') : undefined}
          onBadgeClick={(badge) => {
            if (props.configBadges?.includes(badge)) props.onConfigBadgeClick?.()
            else setIsFiltersPanelOpen(true)
          }}
          // "Clear all" clears filters only, so it shows only while there is a filter to clear.
          onClearAll={props.appliedFilters?.length ? props.onResetAllFilters : undefined}
        />
      </VStack>

      <div className="w-full">
        {/* Content Slot */}
        <AnimatePresence mode="wait" initial={false}>
          {props.contentSlot && (
            <motion.div
              initial={{ height: 0, marginBottom: 0, opacity: 0 }}
              animate={{ height: 'auto', marginBottom: '0.5rem', opacity: 1 }}
              exit={{ height: 0, marginBottom: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: snappyEaseInOut }}
            >
              {props.contentSlot}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-background-level-1 relative w-full rounded-md border">
          <Table className="h-full">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const pinning = getPinningAttributes(header.column, 'header')
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          header.column.columnDef.meta?.headerClassName,
                          pinning.className,
                        )}
                        style={pinning.style}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {props.isPending ? (
                <LoadingState colSpan={props.columns.length} />
              ) : props.error ? (
                <TableMessage
                  message={`${t('errors.default')}: ${props.error.message || t('errors.somethingWentWrong')}`}
                  icon={OctagonXIcon}
                  colSpan={props.columns.length}
                />
              ) : table.getRowModel().rows?.length ? (
                <>
                  {props.totals && (
                    <TotalsRow
                      table={table}
                      totals={props.totals}
                      getCellAttributes={(column) => getPinningAttributes(column, 'cell')}
                    />
                  )}
                  {table.getRowModel().rows.map((row, index, rows) => {
                    const nextRow = rows[index + 1]

                    return (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        data-status={row.original.rowStatus ?? 'default'}
                        data-depth={row.depth}
                        data-expanded={row.getIsExpanded() || undefined}
                        {...(props.onRowClick &&
                          (props.isRowClickable?.(row.original) ?? true) && {
                            role: 'link',
                            tabIndex: 0,
                            onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
                              if (isOutsideRow(event.currentTarget, event.target)) return
                              if (isInteractiveTarget(event.target)) return
                              // A drag to select text ends in a click; navigating away would discard
                              // the selection the reader just made.
                              if (window.getSelection()?.toString()) return
                              props.onRowClick?.(row.original)
                            },
                            onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                              if (isOutsideRow(event.currentTarget, event.target)) return
                              if (event.key !== 'Enter' || isInteractiveTarget(event.target)) return
                              props.onRowClick?.(row.original)
                            },
                          })}
                        className={cn(
                          // Named group so pinned cells can mirror the row's semantic background.
                          'group/row',
                          props.onRowClick &&
                            (props.isRowClickable?.(row.original) ?? true) &&
                            'cursor-pointer',
                          row.depth > 0 && 'bg-background-level-2/80',
                          row.depth > 0 && nextRow?.depth === row.depth && 'border-b-0',
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const pinning = getPinningAttributes(cell.column, 'cell')
                          return (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                cell.column.columnDef.meta?.cellClassName,
                                pinning.className,
                              )}
                              style={pinning.style}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  })}
                </>
              ) : (
                <TableMessage
                  message={t('datatable.noResults')}
                  icon={SearchXIcon}
                  colSpan={props.columns.length}
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Data Table Footer */}
      <Spread align="center">
        <PageSizeSelector
          table={table}
          pageSize={props.pageSize}
          onPageSizeChange={props.onPageSizeChange}
        />
        <PageControls table={table} rowCount={props.rowCount} pageSize={props.pageSize} />
      </Spread>
    </VStack>
  )
}
